import { ConfigService } from './config.service';
import { RoadmapService } from './roadmap.service';
import { SpreadsheetColumnsService } from './spreadsheet-columns.service';
import { SettingsService } from './settings.service';
import { Injectable } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { first, map, shareReplay } from 'rxjs/operators';
import { APIService, KPI } from '../api';
import { AssetsService } from './assets.service';
import { DocumentTypesService } from './document-types.service';
import { InitiativesService } from './initiatives.service';
import { InvestmentCategoriesService } from './investment-categories.service';
import { InvestmentReasonsService } from './investment-reasons.service';
import { PerimetersService } from './perimeters.service';
import { ReferenceDataService, PerimeterReferenceDataMap } from './reference-data.service';
import { idMapToArray, ScopeService } from './scope.service';
import { YearsService } from "./years.service";
import { ActivatedRoute } from "@angular/router";
import { InvestmentScenariiService } from "./investment-scenarii.service";
import { RightPanelDialogService } from './right-panel-dialog.service';
import { IAggFuncParams, IRowNode } from 'ag-grid-community';
import { QuotePartService } from './quote-part.service';
import { SiteUsersFormComponent } from '../../@theme/components/side-forms/site-users-form/site-users-form.component';
import { NbDialogService } from '@nebular/theme';
import { SheetDialogComponent } from '../../@theme/components/sheet-dialog/sheet-dialog.component';

@Injectable()
export class SpreadsheetSitesService extends SpreadsheetColumnsService {

  private lastRequestParams: any = null;
  private lastRequest$: Observable<any[]> | null = null;

  constructor(
    protected documentTypesService: DocumentTypesService,
    protected investmentCategoriesService: InvestmentCategoriesService,
    protected investmentReasonsService: InvestmentReasonsService,
    protected scope: ScopeService,
    protected translate: TranslateService,
    protected api: APIService,
    protected perimetersService: PerimetersService,
    protected referenceDataService: ReferenceDataService,
    protected assetsService: AssetsService,
    protected yearsService: YearsService,
    protected route: ActivatedRoute,
    protected investmentScenariiService: InvestmentScenariiService,
    protected initiativesService: InitiativesService,
    protected roadmapService: RoadmapService,
    private rightPanelDialogService: RightPanelDialogService,
    protected quotePartService: QuotePartService,
    protected config: ConfigService,
    private settingsService: SettingsService,
    private dialogService: NbDialogService
  ) {
    super(
      documentTypesService, investmentCategoriesService, investmentReasonsService,
      scope, translate, api, perimetersService, referenceDataService, assetsService,
      yearsService, route, investmentScenariiService, initiativesService, roadmapService,
      quotePartService, config
    );
  }

  /**
   * A group row (site / level parent) only shows its own reference value.
   * If it has none, the cell stays empty: the value is never taken from a child row.
   */
  private refAgg(params: IAggFuncParams): unknown {
    const field = params.colDef?.field;
    const ownValue = field ? params.rowNode?.data?.[field] : undefined;
    return ownValue === undefined || ownValue === '' ? null : ownValue;
  }

  /**
   * SIMPLE SITES COLUMNS
   */
  public getAgGridSitesColumns(_canEdit: boolean): Observable<(KPI.CustomColDef | KPI.CustomColGroupDef)[]> {
    return combineLatest([
      this.scope.clusters$.pipe(first(), idMapToArray()),
      this.scope.referenceFields$.pipe(first(), idMapToArray()),
      this.perimetersService.perimeters$.pipe(first()),
      this.yearsService.yearsRange$.pipe(first()),
      this.settingsService.decisionEnergyCarbonTrajectoryInMenu$,
      this.settingsService.decisionBouquetTravauxManagement$,
      this.settingsService.assetEquipmentValidation$,
      this.settingsService.siteControlPoints$,
    ]).pipe(
      map(([
        clusters,
        referenceFields,
        sites,
        years,
        showEnergyCarbonTrajectory,
        showBouquetTravaux,
        showMissingData,
        showControlPoints,
      ]) => {
        const refAgg = this.refAgg.bind(this);

        const pmNomRefField = referenceFields.find(
          (refField) => refField.displayed_intelligence && (
            refField.var_name === 'PM_nom' ||
            refField.label === 'PM (name)'
          )
        );

        const siteCodeRefField = referenceFields.find(
          (refField) => refField.displayed_intelligence && (
            refField.var_name === 'code_site' ||
            refField.label === 'Site code'
          )
        );

        const pmNomCol = pmNomRefField
          ? {
              field: `reference_field_${pmNomRefField.id}`,
              headerName: this.translate.instant('PM (name)'),
              hide: true,
              width: 120,
              filter: 'agSetColumnFilter',
              sortable: true,
              aggFunc: refAgg,
              valueFormatter: (params) => {
                const value = params.value;
                return value === null || value === undefined ? '-' : value;
              },
            }
          : null;

        const siteCodeCol = siteCodeRefField
          ? {
              field: `reference_field_${siteCodeRefField.id}`,
              headerName: this.translate.instant('Site code'),
              hide: true,
              width: 120,
              filter: 'agSetColumnFilter',
              sortable: true,
              aggFunc: refAgg,
              valueFormatter: (params) => {
                const value = params.value;
                return value === null || value === undefined ? '-' : value;
              },
            }
          : null;

        const otherRefFieldsCols = referenceFields
          .filter((refField) => 
            refField.displayed_intelligence && 
            refField.id !== pmNomRefField?.id &&
            refField.id !== siteCodeRefField?.id
          )
          .map((referenceField) => {
            let filterType = 'agTextColumnFilter';
            if (referenceField.data_type === KPI.ReferenceFieldDataType.DATA_TYPE_NUMBER) {
              filterType = 'agNumberColumnFilter';
            } else if (referenceField.data_type === KPI.ReferenceFieldDataType.DATA_TYPE_FK || 
                       (referenceField.values && referenceField.values.length > 0)) {
              filterType = 'agSetColumnFilter';
            } else {
              filterType = 'agTextColumnFilter';
            }
            
            return {
              field: `reference_field_${referenceField.id}`,
              hide: true,
              headerName: referenceField.label,
              filter: filterType,
              sortable: true,
              aggFunc: refAgg,
            };
          });

        /**
         * Setup reference data
         */
        clusters.forEach((cluster) => this.clustersMap[cluster.id] = cluster.name);
        sites.forEach((site) => {
          this.perimetersMap[site.id] = site.name;
          site.sub_perimeters.forEach((subPerimeter) => {
            this.perimetersMap[subPerimeter.id] = `${site.name} - ${subPerimeter.name}`;
          });
        });

        const plannedCapexYearColumns: KPI.CustomColDef[] = years.map((year, index) => {
          return {
            field: `planned_budget_${year}`,
            headerName: year.toString(),
            valueFormatter: this.priceFormatter,
            filter: 'agNumberColumnFilter',
            sortable: true,
            hide: index > 5,
            width: 115,
            columnGroupShow: 'open' as const,
            cellStyle: { cursor: 'pointer' },
            onCellClicked: (params) => this.openInvestmentsForSiteYear(params.data, year),
          };
        });

        const plannedCapexGroup: KPI.CustomColGroupDef = {
          groupId: 'planned_capex',
          headerName: this.translate.instant('Planned CAPEX'),
          marryChildren: true,
          children: [
            {
              field: 'empty_planned_capex',
              headerName: '',
              width: 115,
              hide: false,
              lockVisible: true,
              columnGroupShow: 'closed' as const,
              menuTabs: [],
              suppressColumnsToolPanel: true,
              suppressFiltersToolPanel: true,
              filter: false,
            },
            ...plannedCapexYearColumns,
          ],
        };

        return [
          // CLUSTER
          {
            field: 'cluster',
            headerName: this.translate.instant(
              'core.utils.spreadsheet_columns.column_cluster',
            ),
            filter: 'agSetColumnFilter',
            sortable: true,
            aggFunc: 'first',
            hide: true,
            width: 150,
            refData: this.clustersMap,
            valueFormatter: (params) => {
              const value = params.value;
              if (value === null || value === undefined) return '-';
              return this.clustersMap[value] || value;
            },
          },
          // USERS COUNT
          ...(_canEdit ? [{
            field: 'users_count',
            headerName: this.translate.instant('Users'),
            filter: 'agNumberColumnFilter',
            sortable: true,
            width: 120,
            cellRenderer: (params) => {
              const value = params.value;
              if (value === null || value === undefined) return '-';
              if (params.node?.level === 0) {
                const link = document.createElement('a');
                link.style.cursor = 'pointer';
                link.style.textDecoration = 'underline';
                link.textContent = `${value}`;
                return link;
              }
              return `${value}`;
            },
            onCellClicked: (params) => {
              if (params.node?.level === 0 && params.data) {
                this.openSiteUsers(params.data);
              }
            },
          }] : []),
          // EQUIPMENTS COUNT
          {
            field: 'assets_count',
            headerName: this.translate.instant('Equipments Nb'),
            filter: 'agNumberColumnFilter',
            sortable: true,
            valueFormatter: this.nullFormatter,
            hide: false,
            width: 120,
          },
          // CRITICAL EQUIPMENTS COUNT
          {
            field: 'critical_assets_count',
            headerName: this.translate.instant('Of which are in critical state'),
            filter: 'agNumberColumnFilter',
            sortable: true,
            valueFormatter: this.nullFormatter,
            hide: false,
            width: 100,
          },
          plannedCapexGroup,
          // PERCENTAGE CONTROL POINTS
          ...(showControlPoints ? [{
            field: 'percentage_control_points_acknowledged',
            headerName: this.translate.instant('% Control Points OK'),
            filter: 'agNumberColumnFilter',
            sortable: true,
            hide: true,
            width: 120,
            valueGetter: (params) => {
              const value = params.data?.percentage_control_points_acknowledged;
              if (value === null || value === undefined) return null;
              if (typeof value === 'string') {
                const num = parseFloat(value.replace('%', '').trim());
                return isNaN(num) ? null : num;
              }
              return typeof value === 'number' ? value : null;
            },
            valueFormatter: (params) => {
              const value = params.value;
              if (value === null || value === undefined) return '-';
              return `${value}%`;
            },
          }] : []),
          // MISSING DATA
          ...(showMissingData ? [{
            field: 'missing_data_count',
            headerName: this.translate.instant('Missing data'),
            filter: 'agNumberColumnFilter',
            sortable: true,
            hide: false,
            valueFormatter: (params) => {
              const value = params.value;
              return value === 0 || value === null || value === undefined ? '-' : value;
            },
            width: 120,
          }] : []),
          // NUMBER OF CONTROL POINTS
          ...(showControlPoints ? [{
            field: 'nb_control_points',
            headerName: this.translate.instant('Number of Point of Control'),
            filter: 'agNumberColumnFilter',
            sortable: true,
            hide: true,
          }] : []),
          // SURFACE RATIO ENERGIE
          ...(showEnergyCarbonTrajectory ? [{
            field: 'flooring_area',
            headerName: this.translate.instant('Surface ratio energy'),
            filter: 'agNumberColumnFilter',
            sortable: true,
            hide: true,
            width: 130,
            valueGetter: (params) => {
              if (!params.data?.is_building) return null;
              return params.data?.flooring_area ?? null;
            },
            valueFormatter: (params) => {
              const value = params.value;
              return value === null || value === undefined ? '-' : `${value} m²`;
            },
          }] : []),
          // ENERGY OBJECTIVE 2030
          ...(showEnergyCarbonTrajectory ? [{
            field: 'energy_objective_2030',
            headerName: this.translate.instant('Energy Objective 2030'),
            filter: 'agSetColumnFilter',
            sortable: true,
            hide: false,
            width: 130,
            valueFormatter: (params) => {
              const value = params.value;
              return value === null || value === undefined ? '-' : value;
            },
          }] : []),
          // ENERGY OBJECTIVE 2040
          ...(showEnergyCarbonTrajectory ? [{
            field: 'energy_objective_2040',
            headerName: this.translate.instant('Energy Objective 2040'),
            filter: 'agSetColumnFilter',
            sortable: true,
            hide: true,
            width: 130,
            valueFormatter: (params) => {
              const value = params.value;
              return value === null || value === undefined ? '-' : value;
            },
          }] : []),
          // ENERGY OBJECTIVE 2050
          ...(showEnergyCarbonTrajectory ? [{
            field: 'energy_objective_2050',
            headerName: this.translate.instant('Energy Objective 2050'),
            filter: 'agSetColumnFilter',
            sortable: true,
            hide: true,
            width: 130,
            valueFormatter: (params) => {
              const value = params.value;
              return value === null || value === undefined ? '-' : value;
            },
          }] : []),
          // CARBON OBJECTIVE 2030
          ...(showEnergyCarbonTrajectory ? [{
            field: 'carbon_objective_2030',
            headerName: this.translate.instant('Carbon Objective 2030'),
            filter: 'agSetColumnFilter',
            sortable: true,
            hide: true,
            width: 130,
            valueFormatter: (params) => {
              const value = params.value;
              return value === null || value === undefined ? '-' : value;
            },
          }] : []),
          // CARBON OBJECTIVE 2040
          ...(showEnergyCarbonTrajectory ? [{
            field: 'carbon_objective_2040',
            headerName: this.translate.instant('Carbon Objective 2040'),
            filter: 'agSetColumnFilter',
            sortable: true,
            hide: true,
            width: 130,
            valueFormatter: (params) => {
              const value = params.value;
              return value === null || value === undefined ? '-' : value;
            },
          }] : []),
          // CARBON OBJECTIVE 2050
          ...(showEnergyCarbonTrajectory ? [{
            field: 'carbon_objective_2050',
            headerName: this.translate.instant('Carbon Objective 2050'),
            filter: 'agSetColumnFilter',
            sortable: true,
            hide: true,
            width: 130,
            valueFormatter: (params) => {
              const value = params.value;
              return value === null || value === undefined ? '-' : value;
            },
          }] : []),
          // PM (name)
          ...(pmNomCol ? [pmNomCol] : []),
          // Site code
          ...(siteCodeCol ? [siteCodeCol] : []),
          // INVESTMENT PROJECTS
          ...(showBouquetTravaux ? [{
            field: 'investment_projects',
            headerName: this.translate.instant('Investments projects'),
            filter: 'agNumberColumnFilter',
            sortable: true,
            hide: true,
            valueFormatter: this.nullFormatter,
          }] : []),
          // TODO: Implement openPerimeters method to enable this column
          // ...(_canEdit ? [
          //   {
          //     field: 'see',
          //     headerName: this.translate.instant('Perimeters'),
          //     cellRenderer: SeeButtonRendererComponent,
          //     cellRendererParams: {
          //       callback: (params) => this.openPerimeters(params),
          //     },
          //   },
          // ] : []),
          // REFERENCE FIELDS
          ...otherRefFieldsCols,
        ];
      })
    );
  }

  private openSiteUsers(data: any): void {
    const siteId = data.id;
    const siteName = data.site_name || data.name || '';
    this.rightPanelDialogService.open(SiteUsersFormComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      context: { siteId, siteName },
    });
  }

  private openInvestmentsForSiteYear(row: any, year: number): void {
    const investmentIds = row?.planned_investment_ids_by_year?.[year] || [];
    if (!investmentIds.length) return;

    this.investmentScenariiService.mainScenario$.pipe(first()).subscribe((scenario) => {
      const context: any = {
        autoFocus: false,
        investmentsListScenario: scenario,
      };

      if (investmentIds.length === 1) {
        context.investmentId = investmentIds[0];
      } else {
        context.investmentsList = true;
        context.investmentsListFilter = {
          invest_id: investmentIds,
        };
      }

      this.dialogService.open(SheetDialogComponent, { context });
    });
  }

  private getGroupSumOfField(rowNode: IRowNode<any>, colField: string, condition?: Function): number {
    return Math.round(
      rowNode?.childrenAfterFilter?.filter((child) =>
        child.childrenAfterFilter?.length || !condition || condition(child)
      ).reduce((acc, child) =>
        acc + (
          child.childrenAfterFilter?.length
            ? this.getGroupSumOfField(child, colField, condition) || child.data?.[colField] || 0
            : child.data?.[colField] || 0
        )
        , 0
      ) || 0
    );
  }

  private resolveRefValue(
    refData: KPI.PerimeterReferenceValue[],
    varName: string,
  ): KPI.PerimeterReferenceValue | undefined {
    const hasValue = (entry: KPI.PerimeterReferenceValue) =>
      entry.value !== null &&
      entry.value !== undefined &&
      String(entry.value).trim() !== '';

    const candidates = (refData ?? [])
      .filter((rd) => rd.var_name === varName && hasValue(rd))
      .sort((a, b) => (b.year ?? 0) - (a.year ?? 0));

    return candidates.find((rd) => !rd.aggregated) ?? candidates[0];
  }

  public getFirstOfField(rowNode: IRowNode<any>, colField: string, condition?: Function): any {
    const filteredChildren = rowNode?.childrenAfterFilter?.filter((child) =>
      child.childrenAfterFilter?.length || !condition || condition(child)
    );
    return filteredChildren?.length
      ? this.getFirstOfField(filteredChildren?.[0], colField, condition)
      : rowNode?.data?.[colField];
  }

  public getSitesData(
    buildings: number[],
    [start_year, end_year],
    refreshCache: boolean = false,
  ): Observable<any[]> {
    return this.requestSitesData(
      'sites',
      buildings,
      [start_year, end_year],
      refreshCache,
      (options) => this.api.sites_table(options),
    );
  }

  public getAdministrationSitesData(
    buildings: number[],
    [start_year, end_year],
    refreshCache: boolean = false,
  ): Observable<any[]> {
    return this.requestSitesData(
      'administration-sites',
      buildings,
      [start_year, end_year],
      refreshCache,
      (options) => this.api.administration_sites_table(options),
    );
  }

  private requestSitesData(
    endpoint: string,
    buildings: number[],
    [start_year, end_year],
    refreshCache: boolean,
    request: (options: any) => Observable<any[]>,
  ): Observable<any[]> {
    const currentParams = { endpoint, buildings, start_year, end_year };

    if (!refreshCache && this.lastRequest$ && JSON.stringify(this.lastRequestParams) === JSON.stringify(currentParams)) {
      return this.lastRequest$;
    }

    this.lastRequestParams = currentParams;
    this.lastRequest$ = combineLatest([
      this.scope.referenceFields$.pipe(idMapToArray()),
      this.referenceDataService.perimeterReferenceDataMaps$,
      request({
        buildings,
        start_year,
        end_year,
        ...(refreshCache ? { refresh_cache: true } : {}),
      }),
    ]).pipe(
      map(([referenceFields, perimeters, rows]) => {
        return rows
          // .filter((row) => !rows.find((r) => r.level_parent_id === row.id)) // remove level_1 rows if children
          .map((row) => {
            // REFERENCE FIELDS
            // Find perimeter in perimeters, or in perimeters sub_perimeters_ref_data_map (array of perimeters)
            const site = perimeters.find((p) => p.id === row['id'])
              || perimeters.find((p) => p.sub_perimeters_ref_data_map.find((sp) => sp.id === row['id']));
            const usedPerimeter: PerimeterReferenceDataMap = site
              ? (site.id !== row['id']
                ? site.sub_perimeters_ref_data_map.find((sp) => sp.id === row['id'])
                : site)
              : null;
            if (usedPerimeter) {
              referenceFields.map((referenceField) => {
                if (!referenceField.displayed_intelligence) return;
                const refValue = this.resolveRefValue(
                  usedPerimeter.ref_data,
                  referenceField.var_name,
                );
                if (refValue) {
                  row[`reference_field_${referenceField.id}`] = refValue.value;
                }
              });
            }
            // TREE DATA PATH: SITE > LEVEL PARENT > PERIMETER
            row['path'] = [row['site_name'], row['level_parent__name'], row['perimeter_name']].filter((v) => v);

            row['green_capex_percentage'] = row['green_planned_budget'] && row['planned_budget']
              ? Math.round(row['green_planned_budget'] / row['planned_budget'] * 100)
              : null;

            // CUSTOM AGGREGATION AS A PARENT CAN HAVE DATA, WHICH SHOULD BE ADDED TO ITS CHILDREN
            ['assets_count', 'critical_assets_count'].forEach((field) => {
              if (!row['parent_id']) {
                // IF IS SITE
                row[field] = (row[field] || 0)
                  + rows.filter((r) => r['parent_id'] === row['id']).reduce((acc, r) => acc + (r[field] || 0), 0);
              } else if (row['parent_id'] && !row['level_parent_id']) {
                // IF IS LEVEL PARENT
                row[field] = (row[field] || 0)
                  + rows.filter((r) => r['level_parent_id'] === row['id']).reduce((acc, r) => acc + (r[field] || 0), 0);
              }
            });

            return row;
          });
      }),
      shareReplay(1)
    );
    
    return this.lastRequest$;
  }

}
